import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { authService } from '../services/authService';
import { toast } from 'react-toastify';

interface DeleteConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void>;
  entityName: string;
  entityType: string; // 'provider', 'customer', 'user'
  requirePassword?: boolean;
  dependencies?: { canDelete: boolean; reasons: string[] };
}

export const DeleteConfirmationModal: React.FC<DeleteConfirmationModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  entityName,
  entityType,
  requirePassword = true,
  dependencies,
}) => {
  const { t } = useTranslation();
  const [confirmText, setConfirmText] = useState('');
  const [password, setPassword] = useState('');
  const [isVerifyingPassword, setIsVerifyingPassword] = useState(false);
  const [passwordVerified, setPasswordVerified] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [step, setStep] = useState<'confirm' | 'password'>('confirm');

  useEffect(() => {
    if (!isOpen) {
      setConfirmText('');
      setPassword('');
      setPasswordVerified(false);
      setIsDeleting(false);
      setStep('confirm');
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const isNameMatched = confirmText.trim().toLowerCase() === entityName.trim().toLowerCase();
  const canProceed = isNameMatched && (!requirePassword || passwordVerified);

  const handleVerifyPassword = async () => {
    if (!password) return;

    setIsVerifyingPassword(true);
    try {
      const result = await authService.verifyPassword(password);
      if (result.valid) {
        setPasswordVerified(true);
        toast.success(t('deleteConfirmation.passwordVerified'));
      } else {
        toast.error(t('deleteConfirmation.passwordInvalid'));
      }
    } catch (error) {
      toast.error(t('deleteConfirmation.passwordVerifyError'));
    } finally {
      setIsVerifyingPassword(false);
    }
  };

  const handleConfirm = async () => {
    if (!canProceed) return;

    setIsDeleting(true);
    try {
      await onConfirm();
      onClose();
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || t('common.error');
      toast.error(errorMessage);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleNextStep = () => {
    if (isNameMatched && requirePassword) {
      setStep('password');
    } else if (isNameMatched) {
      handleConfirm();
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
              <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                {t('deleteConfirmation.title')}
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {t(`deleteConfirmation.${entityType}Warning`)}
              </p>
            </div>
          </div>

          {/* Dependency warnings */}
          {dependencies && !dependencies.canDelete && (
            <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <p className="text-sm font-medium text-red-800 dark:text-red-300 mb-2">
                {t('deleteConfirmation.cannotDelete')}
              </p>
              <ul className="list-disc list-inside text-sm text-red-700 dark:text-red-400 space-y-1">
                {dependencies.reasons.map((reason, idx) => (
                  <li key={idx}>{reason}</li>
                ))}
              </ul>
            </div>
          )}

          {dependencies?.canDelete !== false && (
            <>
              {step === 'confirm' && (
                <>
                  {/* Name confirmation */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t('deleteConfirmation.typeToConfirm')}
                    </label>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                      {t('deleteConfirmation.typeName')}: <strong className="text-gray-900 dark:text-white">{entityName}</strong>
                    </p>
                    <input
                      type="text"
                      value={confirmText}
                      onChange={(e) => setConfirmText(e.target.value)}
                      placeholder={entityName}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 bg-white dark:bg-gray-700 dark:text-white"
                      autoFocus
                    />
                    {confirmText && !isNameMatched && (
                      <p className="mt-1 text-sm text-red-500">
                        {t('deleteConfirmation.nameMismatch')}
                      </p>
                    )}
                  </div>
                </>
              )}

              {step === 'password' && requirePassword && (
                <>
                  {/* Password verification */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t('deleteConfirmation.enterPassword')}
                    </label>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                      {t('deleteConfirmation.passwordReason')}
                    </p>
                    <div className="flex gap-2">
                      <input
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder={t('auth.password')}
                        className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 bg-white dark:bg-gray-700 dark:text-white"
                        autoFocus
                        disabled={passwordVerified}
                      />
                      {!passwordVerified && (
                        <button
                          onClick={handleVerifyPassword}
                          disabled={!password || isVerifyingPassword}
                          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {isVerifyingPassword ? '...' : t('deleteConfirmation.verify')}
                        </button>
                      )}
                    </div>
                    {passwordVerified && (
                      <p className="mt-1 text-sm text-green-600 flex items-center gap-1">
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                        {t('deleteConfirmation.passwordVerified')}
                      </p>
                    )}
                  </div>
                </>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 px-6 py-4 bg-gray-50 dark:bg-gray-700/50 rounded-b-lg">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600 rounded-lg transition-colors"
          >
            {t('common.cancel')}
          </button>

          {dependencies?.canDelete !== false && (
            <>
              {step === 'confirm' && (
                <button
                  onClick={handleNextStep}
                  disabled={!isNameMatched}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {requirePassword ? t('common.next') : t('deleteConfirmation.confirmDelete')}
                </button>
              )}

              {step === 'password' && (
                <button
                  onClick={handleConfirm}
                  disabled={!canProceed || isDeleting}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {isDeleting ? t('common.loading') : t('deleteConfirmation.confirmDelete')}
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};
